import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, Input, NgModule, OnChanges, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { PowerBIFrame } from '@app/models/power-bi-frame';
import { WINDOW } from '@app/common/app-tokens';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { Platform } from '@angular/cdk/platform';
import { Report, models, service, factories } from 'powerbi-client';

@Component({
  selector: 'app-power-bi-frame',
  templateUrl: './power-bi-frame.component.html',
  styleUrls: ['./power-bi-frame.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PowerBIFrameComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('frameContainer', { static: true }) reportContainer: ElementRef;
  @Input() selectedFrame: PowerBIFrame;
  @Input() accessToken: string;
  mobileBreakepoint = 776; // 776px

  resizeObservable$: Observable<Event>;
  fullScreenObservable$: Observable<Event>;
  errorObservable$: Observable<Event>;
  subscription$: Subscription = new Subscription();

  embedConfig: models.IReportEmbedConfiguration | models.IDashboardEmbedConfiguration | models.ITileEmbedConfiguration;

  frame: Report;
  powerBI: service.Service;
  isMobileDevice = false;

  private initialized = false;

  constructor(@Inject(WINDOW) private window: Window, private platform: Platform) {
    this.isMobileDevice = this.platform.ANDROID || this.platform.IOS || window.innerWidth < this.mobileBreakepoint;
    this.powerBI = new service.Service(factories.hpmFactory, factories.wpmpFactory, factories.routerFactory);
  }

  ngOnInit() {

    this.resizeObservable$ = fromEvent(window, 'resize');

    this.subscription$.add(
      this.resizeObservable$.pipe(
        debounceTime(300))
        .subscribe(() => {
          if (this.window.innerWidth < this.mobileBreakepoint && !this.isMobileDevice) {
            this.updateMobileViewSettings(true);
            this.isMobileDevice = true;
          } else if (this.window.innerWidth >= this.mobileBreakepoint && this.isMobileDevice) {
            this.updateMobileViewSettings(false);
            this.isMobileDevice = false;
          }
        }));
  }

  ngOnChanges() {
    if (this.reportContainer.nativeElement) {
      // The Power BI Service needs to be reset if new visual is provided.
      this.powerBI.reset(this.reportContainer.nativeElement);
      this.setPowerBIFrame(true);
    }
  }

  ngAfterViewInit() {
    this.errorObservable$ = fromEvent(this.frame, 'error');

    this.subscription$.add(this.errorObservable$.subscribe(evt => {
      console.log(evt);
    }));
  }

  ngOnDestroy() {
    this.subscription$ && this.subscription$.unsubscribe();
  }

  setPowerBIFrame(rebuildConfig: boolean = false) {
    rebuildConfig && this.buildEmbedConfig();

    this.frame = this.powerBI.embed(this.reportContainer.nativeElement, this.embedConfig) as Report;
    this.frame.setComponentTitle(this.selectedFrame.Title);
  }

  updateMobileViewSettings(enableMobileView: boolean) {
    if (this.selectedFrame.Type === 0) {
      this.embedConfig.settings.layoutType = enableMobileView ? models.LayoutType.MobilePortrait : models.LayoutType.Custom;
    }
    if (this.selectedFrame.Type === 1) {
      (this.embedConfig as models.IDashboardEmbedConfiguration).pageView = enableMobileView ? 'oneColumn' : 'fitToWidth';
    }
    this.powerBI.reset(this.reportContainer.nativeElement);
    this.setPowerBIFrame();
  }

  getClassForMobileView() {
    // MobileViewHeight is specified in add/edit form.
    // padding-top: x%: percentages are relative to the width of the frame container.
    return {
      'padding-top': `${this.selectedFrame.MobileViewHeight}vh`
    };
  }

  buildEmbedConfig() {

    if (this.selectedFrame.Type === 0) {
      this.embedConfig = {
        type: 'report',
        tokenType: models.TokenType.Aad,
        accessToken: this.accessToken,
        embedUrl: this.selectedFrame.EmbedUrl,
        id: this.selectedFrame.EmbedId,
        permissions: models.Permissions.Read,
        viewMode: models.ViewMode.View,
        bookmark: this.selectedFrame.BookmarkId ? { name: this.selectedFrame.BookmarkId } : undefined,
        settings: {
          filterPaneEnabled: this.selectedFrame.ShowFilterPane ? true : false,
          navContentPaneEnabled: this.selectedFrame.ShowNavigationPane ? true : false,
          layoutType: this.isMobileDevice ? models.LayoutType.MobilePortrait : models.LayoutType.Custom,
          background: models.BackgroundType.Transparent,
        } as models.ISettings
      } as models.IReportEmbedConfiguration;

    } else if (this.selectedFrame.Type === 1) {
      this.embedConfig = {
        type: 'dashboard',
        tokenType: models.TokenType.Aad,
        accessToken: this.accessToken,
        embedUrl: this.selectedFrame.EmbedUrl,
        id: this.selectedFrame.EmbedId,
        permissions: models.Permissions.Read,
        viewMode: models.ViewMode.View,
        pageView: this.isMobileDevice ? 'oneColumn' : 'fitToWidth',
        settings: {
          background: models.BackgroundType.Transparent,
        } as models.ISettings
      } as models.IDashboardEmbedConfiguration;
    }
  }

}

@NgModule({
  declarations: [PowerBIFrameComponent],
  exports: [PowerBIFrameComponent],
  imports: [
    CommonModule,
    MatButtonModule
  ]
})

export class PowerBIFrameModule { }
